import React from 'react';
import { FaCheck } from 'react-icons/fa';

// Claim Free Resource is no longer a step — it lives in the dashboard sidebar cards.
export const STEP_LABELS = ['Analysis', 'NDA', 'Call', 'Program'];

export const CtaArrow = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

export function StartStepIndicatorColumn({ step, currentStep, label, onStepClick }) {
  const status = currentStep === step ? 'active' : currentStep < step ? 'inactive' : 'complete';
  const isActive = currentStep === step;
  const isDone = currentStep > step;

  const circleClass =
    status === 'inactive'
      ? 'bg-gray-500 dark:bg-gray-600'
      : 'bg-red-600 dark:bg-red-600';

  return (
    <div className="flex flex-1 flex-col items-center min-w-0 basis-0">
      <span
        className={`text-[11px] sm:text-xs md:text-sm text-center mb-2 w-full leading-snug px-0.5 ${
          isActive
            ? 'font-semibold text-red-600 dark:text-red-400'
            : isDone
              ? 'text-gray-500 dark:text-gray-400'
              : 'text-gray-400 dark:text-gray-500'
        }`}
      >
        {label}
      </span>
      <button
        type="button"
        onClick={() => {
          if (step !== currentStep) onStepClick(step);
        }}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${circleClass}`}
        aria-current={isActive ? 'step' : undefined}
        aria-label={`Step ${step}: ${label}`}
      >
        {status === 'complete' ? (
          <FaCheck className="h-4 w-4 text-white" aria-hidden />
        ) : status === 'active' ? (
          <span className="h-3 w-3 rounded-full bg-white block" aria-hidden />
        ) : (
          <span className="text-sm text-white">{step}</span>
        )}
      </button>
    </div>
  );
}

// The oversized glyph sitting behind the CTA. Emoji rather than an icon font, so
// it carries its own color — the tint classes the old react-icons version used
// don't apply, only the size and the knocked-back opacity.
const TASK_TILE_EMOJI_CLASS =
  'absolute inset-0 flex items-center justify-center text-[6.5rem] sm:text-[7.5rem] md:text-[8.5rem] leading-none select-none pointer-events-none';

export function StartTaskActionTile({ emoji, label, onClick, disabled = false, preview = false }) {
  const Tag = preview ? 'div' : 'button';
  const tagProps = preview
    ? { className: 'relative w-full min-h-[10.5rem] sm:min-h-[11.5rem] md:min-h-[12.5rem] rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 flex items-center justify-center p-4 pointer-events-none overflow-hidden' }
    : {
        type: 'button',
        onClick,
        disabled,
        className:
          'relative w-full min-h-[10.5rem] sm:min-h-[11.5rem] md:min-h-[12.5rem] rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-red-300 dark:hover:border-red-700 hover:shadow-lg transition-all flex items-center justify-center p-4 disabled:opacity-50 disabled:cursor-not-allowed group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 overflow-hidden'
      };

  return (
    <Tag {...tagProps}>
      <span
        className={`${TASK_TILE_EMOJI_CLASS} opacity-[0.28] ${preview ? '' : 'group-hover:opacity-[0.38] transition-opacity'}`}
        aria-hidden="true"
      >
        {emoji}
      </span>
      <span className="relative z-10 inline-flex items-center justify-center gap-2 px-4 py-3.5 min-w-[9.5rem] max-w-[95%] bg-red-600 text-white font-semibold rounded-lg text-sm sm:text-base shadow-md">
        <span className="text-center leading-snug">{label}</span>
        <CtaArrow />
      </span>
    </Tag>
  );
}

export function StartTaskStatusTile({ emoji, label, variant = 'success' }) {
  const styles =
    variant === 'warning'
      ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50/80 dark:bg-amber-950/30'
      : 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/80 dark:bg-emerald-950/30';
  const textColor =
    variant === 'warning'
      ? 'text-amber-800 dark:text-amber-200'
      : 'text-emerald-800 dark:text-emerald-200';

  return (
    <div
      className={`relative w-full min-h-[10.5rem] sm:min-h-[11.5rem] md:min-h-[12.5rem] rounded-xl border-2 flex flex-col items-center justify-center p-4 text-center gap-2 overflow-hidden ${styles}`}
    >
      <span className={`${TASK_TILE_EMOJI_CLASS} opacity-[0.28]`} aria-hidden="true">
        {emoji}
      </span>
      <span className={`relative z-10 text-sm font-semibold leading-snug ${textColor}`}>{label}</span>
    </div>
  );
}

export function StartTaskStepContent({ stepNumber, title, subtitle, highlight, action }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 md:gap-6 items-center py-3">
      <div className="min-w-0">
        <div className="flex justify-center mb-3">
          <span
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-600 dark:bg-red-600 text-white text-xl font-bold shadow-sm"
            aria-hidden
          >
            {stepNumber}
          </span>
        </div>
        <h2 className="text-lg md:text-xl font-bold text-gray-800 dark:text-white text-center">{title}</h2>
        {highlight ? (
          <p className="text-sm font-semibold text-red-600 dark:text-red-400 mt-1 text-center">{highlight}</p>
        ) : null}
        <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mt-1 leading-relaxed text-center">
          {subtitle}
        </p>
      </div>
      {action ? <div className="flex justify-center w-full min-w-0">{action}</div> : null}
    </div>
  );
}
